import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import Header from "../../components/Header";
import RailSixSigmaPanel from "../../components/RailSixSigmaPanel";
import { useAuth } from "../../context/AuthContext";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
    Panel, Field, DarkHeader, DateClockChips, statusPill,
    fmtDt, fmtTime, IcnTrain, IcnBell, IcnCrane, IcnCheck, IcnYard,
    FlowStep, FlowArrow, NotificationBell, WagonPipelineRibbon, BatchPresetButtons,
    ShortfallBanner,
} from "./shared";
import { Link } from "react-router-dom";

const H_NAVY = "#0B1A3B";

export default function GrindrodCoordinatorDashboard() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
    const [selected, setSelected] = useState(null);
    const [events, setEvents] = useState([]);
    const [releaseCount, setReleaseCount] = useState("");
    const [tippedCount, setTippedCount] = useState("");

    const load = useCallback(async () => {
        try {
            const [cs, nt] = await Promise.all([
                api.get("/rail/consignments?include_completed=true"),
                api.get("/rail/notifications?limit=30"),
            ]);
            setRows(cs.data || []);
            setNotifs(nt.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

    const incoming = rows.filter((c) => c.status === "in_transit_to_grindrod");
    const atGrindrod = rows.filter((c) =>
        ["received_at_grindrod", "waiting_for_tipping", "tipping_in_progress"].includes(c.status));
    // Keep `active` bound to the freshest row for the selected id — without
    // this the wagon counters (wagons_at_grindrod etc.) stayed on the
    // pre-receive snapshot and the release button reported "0 available".
    const active = useMemo(() => {
        if (selected?.id) {
            const fresh = rows.find((r) => r.id === selected.id);
            if (fresh) return fresh;
        }
        return atGrindrod[0] || incoming[0];
    }, [rows, selected?.id, atGrindrod, incoming]);

    useEffect(() => {
        if (!active) return;
        api.get(`/rail/consignments/${active.id}`)
            .then((r) => setEvents(r.data.events || []))
            .catch(() => setEvents([]));
    }, [active?.id, active?.last_update_at]);

    const doAction = async (c, action, body = {}, msg = "Updated") => {
        if (!c) return;
        try {
            await api.post(`/rail/consignments/${c.id}/action`, { action, ...body });
            toast.success(msg); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const total = active?.wagon_count || 0;
    const released = active?.wagons_released || 0;
    const tipped = active?.wagons_tipped || 0;
    const inTipping = Math.max(0, released - tipped);
    const waiting = Math.max(0, total - released);
    const pctTipped = total ? Math.round(tipped * 100 / total) : 0;
    const remaining = total - tipped;

    const donutData = useMemo(() => [
        { name: "Tipped",   value: tipped,    color: "#16A34A" },
        { name: "Tipping",  value: inTipping, color: "#2563EB" },
        { name: "Released", value: 0,         color: "#EA580C" }, // in-flight buffer, kept for legend parity
        { name: "Waiting",  value: waiting,   color: "#94A3B8" },
    ], [tipped, inTipping, waiting]);

    const canReceive  = active?.status === "in_transit_to_grindrod";
    const canWagon    = ["waiting_for_tipping", "tipping_in_progress"].includes(active?.status);
    const canComplete = active?.status === "tipping_in_progress" && tipped >= total && total > 0;

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1600px] mx-auto px-3 md:px-6 py-4 space-y-3">
                <DarkHeader
                    title="Grindrod Rail Coordinator Dashboard"
                    subtitle="Grindrod Site (Tippler)"
                    tint={H_NAVY} icon={<IcnTrain color="#2563EB" size={36} />}
                    right={<DateClockChips notifBtn={
                        <NotificationBell notifs={notifs} onChange={load} testid="gr-notif-btn" />
                    } />}
                />

                <ShortfallBanner testid="gr-shortfalls" />
                {active && <WagonPipelineRibbon c={active} />}

                <div className="flex justify-end">
                    <Link to="/rail-consignments"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[var(--grindrod-navy)] hover:bg-[#1a2a55] text-white text-[11px] font-black uppercase tracking-widest transition"
                          data-testid="gr-view-consignments-btn">
                        📋 View All Consignments
                    </Link>
                </div>

                {/* Row 1 — 4 headline panels */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Panel title="Incoming Train" testid="gr-incoming">
                        {incoming[0] ? (
                            <div className="text-xs space-y-1">
                                <Row2 a="Consignment No" b={incoming[0].consignment_no} />
                                <Row2 a="Train ID" b={incoming[0].train_id || "—"} />
                                <div className="grid grid-cols-3 gap-1 text-center pt-1">
                                    <Field2 label="Wagons" v={incoming[0].wagon_count} />
                                    <Field2 label="Tons"   v={Number(incoming[0].tonnage || 0).toLocaleString()} />
                                    <Field2 label="ETA"    v={fmtTime(incoming[0].eta_grindrod_arrival_at)} />
                                </div>
                                <button onClick={() => { setSelected(incoming[0]); doAction(incoming[0], "receive_at_grindrod", {}, "Train received"); }}
                                        data-testid="gr-receive-btn"
                                        className="mt-2 w-full py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-[10px]">
                                    Receive Train
                                </button>
                            </div>
                        ) : <div className="text-xs text-slate-500 py-6 text-center">No incoming trains.</div>}
                    </Panel>

                    <Panel title="Train Status" testid="gr-status">
                        {active ? (
                            <div className="text-xs space-y-1">
                                <Row2 a="Status" b={statusPill(active.status)} />
                                <Row2 a="Arrived" b={fmtTime(active.actual_grindrod_arrival_at)} />
                                <Row2 a="Receiving Time" b={fmtTime(active.actual_grindrod_arrival_at)} />
                                <button onClick={() => canReceive && doAction(active, "receive_at_grindrod", {}, "Received")}
                                        disabled={!canReceive} data-testid="gr-status-receive"
                                        className="mt-2 w-full py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-40">
                                    ✓ Train Received
                                </button>
                            </div>
                        ) : <div className="text-xs text-slate-500 py-6 text-center">Pick a train.</div>}
                    </Panel>

                    <Panel title="Tipping Progress (Current Train)" testid="gr-progress">
                        <div className="flex items-center gap-2">
                            <div className="w-24 h-24 flex-shrink-0 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={donutData} innerRadius={26} outerRadius={40} paddingAngle={2} dataKey="value">
                                            {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 grid place-items-center text-center pointer-events-none">
                                    <div>
                                        <div className="text-[10px] font-black">{tipped}/{total}</div>
                                        <div className="text-[8px] uppercase text-slate-500">Tipped</div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-[10px] flex-1 space-y-0.5">
                                <Leg color="#16A34A" label="Tipped"   v={`${tipped} (${pct(tipped, total)}%)`} />
                                <Leg color="#2563EB" label="Tipping"  v={`${inTipping} (${pct(inTipping, total)}%)`} />
                                <Leg color="#EA580C" label="Released" v={`${released} (${pct(released, total)}%)`} />
                                <Leg color="#94A3B8" label="Waiting"  v={`${waiting} (${pct(waiting, total)}%)`} />
                            </div>
                        </div>
                    </Panel>

                    <Panel title="Train Completion" testid="gr-completion">
                        <div className="grid grid-cols-2 gap-2 text-xs text-center">
                            <Field2 label="Total Wagons" v={total} />
                            <Field2 label="Tipped"       v={`${tipped} (${pctTipped}%)`} />
                        </div>
                        <button onClick={() => canComplete && doAction(active, "complete_consignment", {}, "Consignment completed")}
                                disabled={!canComplete} data-testid="gr-complete-btn"
                                className="mt-2 w-full py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-40">
                            ✓ Complete Consignment
                        </button>
                        {active?.status === "completed" && (
                            <button onClick={() => doAction(active, "release_empties", {}, "Empties released")}
                                    className="mt-2 w-full py-2 rounded bg-amber-500 hover:bg-amber-600 text-white font-black uppercase tracking-widest text-[10px]">
                                Release Empties
                            </button>
                        )}
                    </Panel>
                </div>

                {/* Row 2 — Wagon Release Control + Trains at Grindrod */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <Panel title="Wagon Release Control" testid="gr-release-control">
                        <div className="grid grid-cols-4 gap-2 mb-3 text-center text-xs">
                            <div className="rounded border p-2 bg-white">
                                <div className="text-[9px] uppercase tracking-widest text-slate-500">Total</div>
                                <div className="text-3xl font-black">{total}</div>
                            </div>
                            <div className="rounded border p-2 bg-orange-50">
                                <div className="text-[9px] uppercase tracking-widest text-orange-700">Released</div>
                                <div className="text-3xl font-black text-orange-700">{released}</div>
                            </div>
                            <div className="rounded border p-2 bg-emerald-50">
                                <div className="text-[9px] uppercase tracking-widest text-emerald-700">Tipped</div>
                                <div className="text-3xl font-black text-emerald-700">{tipped}</div>
                            </div>
                            <div className="rounded border p-2 bg-slate-50">
                                <div className="text-[9px] uppercase tracking-widest text-slate-500">Remaining</div>
                                <div className="text-3xl font-black">{remaining}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="border rounded p-3">
                                <div className="flex justify-between text-xs mb-2">
                                    <span>Wagons available to release</span>
                                    <b>{Math.max(0, (active?.wagons_at_grindrod || 0) - released)}</b>
                                </div>
                                <Field label="Number of wagons to release">
                                    <input type="number" min={1} max={Math.max(0, (active?.wagons_at_grindrod || 0) - released)} value={releaseCount}
                                           onChange={(e) => setReleaseCount(e.target.value)}
                                           data-testid="gr-release-count" className="g-input" />
                                    <BatchPresetButtons
                                        max={Math.max(0, (active?.wagons_at_grindrod || 0) - released)}
                                        onPick={(n) => setReleaseCount(String(n))}
                                        testidPrefix="gr-release" />
                                </Field>
                                <button onClick={() => {
                                            if (!canWagon) return;
                                            const cap = Math.max(0, (active?.wagons_at_grindrod || 0) - released);
                                            const n = Number(releaseCount) || cap;
                                            if (!n) return toast.error("No wagons available to release yet");
                                            doAction(active, "release_wagons",
                                                     { count: n },
                                                     `${n} wagons released`)
                                                .then(() => setReleaseCount(""));
                                        }}
                                        disabled={!canWagon || (Math.max(0, (active?.wagons_at_grindrod || 0) - released) === 0)}
                                        data-testid="gr-release-btn"
                                        className="mt-3 w-full py-2 rounded bg-orange-500 hover:bg-orange-600 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-40">
                                    ✈ Release Wagons
                                </button>
                            </div>
                            <div className="border rounded p-3">
                                <div className="flex justify-between text-xs mb-2"><span>Wagons pending tip</span><b>{inTipping}</b></div>
                                <Field label="Wagons tipped (record)">
                                    <input type="number" min={1} max={inTipping} value={tippedCount}
                                           onChange={(e) => setTippedCount(e.target.value)}
                                           data-testid="gr-tipped-count" className="g-input" />
                                    <BatchPresetButtons max={inTipping}
                                        onPick={(n) => setTippedCount(String(n))}
                                        testidPrefix="gr-tipped" />
                                </Field>
                                <button onClick={() => tippedCount && doAction(active, "record_tipped",
                                                                                  { count: Number(tippedCount) },
                                                                                  `${tippedCount} wagons tipped`)
                                                       .then(() => setTippedCount(""))}
                                        disabled={!canWagon || !tippedCount}
                                        data-testid="gr-tipped-btn"
                                        className="mt-3 w-full py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-40">
                                    Record Tipped
                                </button>
                            </div>
                        </div>
                    </Panel>

                    <Panel title="Trains at Grindrod" testid="gr-trains">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr className="text-left border-b">
                                        <th className="pb-1 pr-2">Consignment</th>
                                        <th className="pb-1 pr-2">Train</th>
                                        <th className="pb-1 pr-2 text-right">Wagons</th>
                                        <th className="pb-1 pr-2 text-right">Tons</th>
                                        <th className="pb-1 pr-2">Status</th>
                                        <th className="pb-1 pr-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {atGrindrod.map((c) => (
                                        <tr key={c.id} className={`border-b cursor-pointer ${active?.id === c.id ? "bg-amber-50" : ""}`}
                                            onClick={() => setSelected(c)}
                                            data-testid={`gr-row-${c.consignment_no}`}>
                                            <td className="py-1 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1 pr-2 text-right">{c.wagon_count}</td>
                                            <td className="py-1 pr-2 text-right">{Number(c.tonnage || 0).toLocaleString()}</td>
                                            <td className="py-1 pr-2">{statusPill(c.status)}</td>
                                            <td className="py-1 pr-2">
                                                <button onClick={(e) => { e.stopPropagation(); setSelected(c); }}
                                                        className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-black">VIEW</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {atGrindrod.length === 0 && (
                                        <tr><td colSpan={6} className="py-4 text-center text-slate-500">No trains at Grindrod.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>

                {/* Row 3 — Tipping events + Train Details + Quick actions */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Panel title={`Tipping Events${active ? " — " + active.consignment_no : ""}`} testid="gr-events">
                        <div className="max-h-52 overflow-y-auto space-y-2 text-[11px]">
                            {events.slice().reverse().slice(0, 8).map((e) => (
                                <div key={e.id} className="flex items-start justify-between gap-2 border-b last:border-0 pb-1.5">
                                    <div className="min-w-0">
                                        <div className="font-bold">{e.event.replace(/_/g," ")}</div>
                                        {e.count != null && <div className="text-slate-500 text-[10px]">Count: {e.count}</div>}
                                    </div>
                                    <div className="text-slate-500 font-mono text-[10px] whitespace-nowrap">{fmtDt(e.at)}</div>
                                </div>
                            ))}
                            {events.length === 0 && <div className="text-slate-500 py-4 text-center">No events yet.</div>}
                        </div>
                    </Panel>

                    <Panel title={`Train Details${active ? " — " + (active.train_id || active.consignment_no) : ""}`} testid="gr-details">
                        {active ? (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <Info label="Consignment"  value={active.consignment_no} />
                                <Info label="Client"       value={active.client_name} />
                                <Info label="Train ID"     value={active.train_id || "—"} />
                                <Info label="From"         value="Transnet Yard" />
                                <Info label="Received"     value={fmtDt(active.actual_grindrod_arrival_at)} />
                                <Info label="Total Wagons" value={total} />
                                <Info label="Coal Grade"   value={active.coal_grade || active.product} />
                                <Info label="Tons"         value={Number(active.tonnage || 0).toLocaleString()} />
                            </div>
                        ) : <div className="text-xs text-slate-500 py-4 text-center">Pick a train.</div>}
                    </Panel>

                    <Panel title="Quick Actions" testid="gr-quick">
                        <div className="grid grid-cols-2 gap-2 text-[10px] font-black uppercase tracking-widest">
                            <button onClick={() => incoming[0] && doAction(incoming[0], "receive_at_grindrod", {}, "Received")}
                                    className="p-2.5 rounded bg-blue-50 text-blue-800">🚆 Receive Train</button>
                            <button onClick={() => canWagon && setReleaseCount("10")}
                                    className="p-2.5 rounded bg-orange-50 text-orange-800">✈ Release Wagons</button>
                            <button onClick={() => canWagon && setTippedCount(String(Math.min(inTipping, 5)))}
                                    className="p-2.5 rounded bg-emerald-50 text-emerald-800">✓ Update Tipping</button>
                            <button onClick={() => canComplete && doAction(active, "complete_consignment", {}, "Completed")}
                                    className="p-2.5 rounded bg-violet-50 text-violet-800">⚑ Complete</button>
                            <div className="col-span-2 p-2.5 rounded bg-slate-100 text-slate-600 text-center">↺ Report Delay / Issue</div>
                        </div>
                    </Panel>
                </div>

                {/* Process flow strip */}
                {active && (
                    <Panel title={`Process Flow — ${active.train_id || active.consignment_no}`} testid="gr-processflow">
                        <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
                            <FlowStep label="Dispatched from Mine" sub={fmtTime(active.actual_dispatch_at)}
                                      done={!!active.actual_dispatch_at} icon="🚂" />
                            <FlowArrow />
                            <FlowStep label="Received Transnet" sub={fmtTime(active.actual_transnet_arrival_at)}
                                      done={!!active.actual_transnet_arrival_at} icon={<IcnYard size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Released to Grindrod" sub={fmtTime(active.actual_transnet_release_at)}
                                      done={!!active.actual_transnet_release_at} icon={<IcnTrain size={22} color="#EA580C" />} />
                            <FlowArrow />
                            <FlowStep label="At Grindrod (Tipping)" sub={active.actual_grindrod_arrival_at ? "Since " + fmtTime(active.actual_grindrod_arrival_at) : "—"}
                                      active={active.status === "tipping_in_progress"} icon={<IcnCrane size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Completed" sub={fmtTime(active.actual_completion_at)}
                                      done={!!active.actual_completion_at} icon={<IcnCheck size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Empty Wagons Returning"
                                      sub={active.empty_released_at ? fmtTime(active.empty_released_at) : "—"}
                                      done={!!active.empty_released_at} icon="↩" />
                        </div>
                    </Panel>
                )}

                {/* Live Six-Sigma Analysis + production recommendations */}
                <RailSixSigmaPanel role="grindrod" />

                <div className="text-[10px] text-slate-400 pb-6">
                    Signed in as <b>{user?.name}</b>.
                </div>
            </main>
        </div>
    );
}
function pct(a, b) { const t = Number(b || 0); const n = Number(a || 0); return t ? Math.round(n * 100 / t) : 0; }
function Row2({ a, b }) { return <div className="flex justify-between border-b py-1"><span className="text-slate-500">{a}</span><b>{b}</b></div>; }
function Field2({ label, v }) { return <div><div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div><div className="text-lg font-black">{v}</div></div>; }
function Info({ label, value }) { return <div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div><div className="font-bold">{value}</div></div>; }
function Leg({ color, label, v }) { return <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} /><span className="flex-1">{label}</span><b>{v}</b></div>; }
